import React from 'react';
import { WIDGET_OPACITY_LEVELS, DEFAULT_WIDGET_OPACITY } from '../../constants/glass';
import { renderFormattedText } from '../../utils/formatText';

/**
 * Blur levels matching the glass effect slider
 */
const BLUR_VALUES = [0, 4, 8, 12, 16, 20];

/**
 * Get combined blur + opacity style object for widget
 * Includes GPU acceleration to prevent rendering artifacts with backdrop-filter
 * @param {number} blur - Blur level index
 * @param {number} opacity - Opacity level index
 * @param {Object} [pc] - Palette render colors
 */
function getWidgetStyle(blur, opacity, pc) {
  const blurValue = BLUR_VALUES[blur ?? 3];
  const opacityValue = WIDGET_OPACITY_LEVELS[opacity ?? DEFAULT_WIDGET_OPACITY]?.opacity ?? 0.05;
  
  const bg = pc ? pc.tint(opacityValue) : `rgba(255, 255, 255, ${opacityValue})`;

  // When widget blur is "None", the widget backing disappears entirely:
  // no background, no backdrop-filter — just the content.
  if (blurValue === 0) {
    return {};
  }

  return {
    willChange: 'backdrop-filter, transform',
    transform: 'translateZ(0)',
    backgroundColor: bg,
    backdropFilter: `blur(${blurValue}px)`,
    WebkitBackdropFilter: `blur(${blurValue}px)`,
  };
}

/**
 * Get border style for widgets — palette-aware
 */
function widgetBorderStyle(showBorder, pc) {
  if (!showBorder) return '';
  // Return className for border width, inline style handles color
  return ' border';
}

function widgetBorderColor(pc) {
  return pc ? pc.tint(0.10) : 'rgba(255,255,255,0.10)';
}

/**
 * Mini stat widget showing label and value
 */
export function MiniStat({ label, value, stat, blur, opacity, showBorder = true, pc, fontSize }) {
  // Support both spread props (label, value) and single stat object
  const statLabel = label ?? stat?.label;
  const statValue = value ?? stat?.value;
  // Scale label/value font sizes from the provided base (default 20px label, 28px value)
  const labelPx = fontSize || 20;
  const valuePx = Math.round(labelPx * 1.4);
  return (
    <div 
      className={`rounded-2xl px-5 py-4${widgetBorderStyle(showBorder, pc)}`}
      style={{ ...getWidgetStyle(blur, opacity, pc), borderColor: showBorder ? widgetBorderColor(pc) : undefined }}
    >
      <div className="tracking-[0.18em] uppercase" style={{ fontSize: `${labelPx}px`, color: pc ? pc.el.statLabel : 'rgba(255,255,255,0.55)' }}>{statLabel}</div>
      <div className="mt-2 font-semibold" style={{ fontSize: `${valuePx}px`, color: pc ? pc.el.statValue : 'rgba(255,255,255,0.90)' }}>{statValue}</div>
    </div>
  );
}

/**
 * Progress bar widget with label and amounts
 */
export function ProgressBar({ label, current, total, blur, opacity, showBorder = true, pc }) {
  const pct = Math.max(0, Math.min(1, total > 0 ? current / total : 0));
  const pctText = `${Math.round(pct * 1000) / 10}%`;
  
  return (
    <div 
      className={`mt-6 rounded-2xl p-5${widgetBorderStyle(showBorder, pc)}`}
      style={{ ...getWidgetStyle(blur, opacity, pc), borderColor: showBorder ? widgetBorderColor(pc) : undefined }}
    >
      <div className="flex items-center justify-between text-[22px]" style={{ color: pc ? pc.el.progressLabel : 'rgba(255,255,255,0.65)' }}>
        <div>{label}</div>
        <div className="tabular-nums font-medium">{pctText}</div>
      </div>
      <div className="mt-3 h-4 w-full rounded-full overflow-hidden" style={{ backgroundColor: pc ? pc.el.progressTrack : 'rgba(255,255,255,0.10)' }}>
        <div 
          className="h-full rounded-full" 
          style={{ width: `${pct * 100}%`, backgroundColor: pc ? pc.el.progressFill : 'rgba(16,185,129,0.60)' }} 
        />
      </div>
      <div className="mt-3 text-[20px] tabular-nums" style={{ color: pc ? pc.el.progressValue : 'rgba(255,255,255,0.50)' }}>
        €{current.toLocaleString()} / €{total.toLocaleString()}
      </div>
    </div>
  );
}

/**
 * Callout/quote widget with label and text
 */
export function Callout({ label, text, blur, opacity, showBorder = true, pc }) {
  return (
    <div 
      className={`mt-6 rounded-2xl p-6${widgetBorderStyle(showBorder, pc)}`}
      style={{ ...getWidgetStyle(blur, opacity, pc), borderColor: showBorder ? widgetBorderColor(pc) : undefined }}
    >
      <div className="text-[20px] tracking-[0.18em] uppercase font-medium" style={{ color: pc ? pc.el.calloutLabel : 'rgba(255,255,255,0.60)' }}>{label}</div>
      <div className="mt-3 text-[26px] leading-relaxed" style={{ color: pc ? pc.el.calloutText : 'rgba(255,255,255,0.80)' }}>{text}</div>
    </div>
  );
}

/**
 * Image placeholder when no image is uploaded
 */
function ImagePlaceholder() {
  return (
    <div className="h-full w-full flex items-center justify-center bg-black/30 text-white/30">
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  );
}

/**
 * Image/media widget with label, caption, and configurable aspect ratio & size
 */
export function ImageWidget({ label, caption, src, aspectRatio = '1:1', size = 'large', blur, opacity, showBorder = true, fontPreset, expandVertical = false, pc }) {
  const [imgOk, setImgOk] = React.useState(true);
  const hasImage = !!src && imgOk;
  
  React.useEffect(() => {
    setImgOk(true);
  }, [src]);

  const isLandscape = aspectRatio === '16:9';
  const isExpanded = isLandscape && expandVertical;

  const getImageWidth = () => {
    if (isLandscape) {
      return size === 'large' ? '100%' : '75%';
    } else {
      return size === 'large' ? '50%' : '30%';
    }
  };

  const innerBorderColor = pc ? pc.tint(0.10) : 'rgba(255,255,255,0.10)';
  const imageContainerClass = "overflow-hidden rounded-2xl border";

  return (
    <div 
      key={`img-${aspectRatio}-${size}-${expandVertical}`}
      className={`mt-6 rounded-2xl p-6${widgetBorderStyle(showBorder, pc)}`}
      style={{ ...getWidgetStyle(blur, opacity, pc), borderColor: showBorder ? widgetBorderColor(pc) : undefined }}
    >
      {isLandscape ? (
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            {label && (
              <div className="text-[20px] tracking-[0.18em] uppercase font-medium" style={{ fontFamily: fontPreset?.subtitle, color: pc ? pc.el.widgetLabel : 'rgba(255,255,255,0.60)' }}>{label}</div>
            )}
            {caption && (
              <div className="mt-2 text-[26px] leading-relaxed whitespace-pre-line" style={{ fontFamily: fontPreset?.body, color: pc ? pc.el.widgetCaption : 'rgba(255,255,255,0.80)' }}>{renderFormattedText(caption)}</div>
            )}
          </div>
          <div className={`${size === 'small' ? 'flex justify-center' : ''}`}>
            <div 
              className={imageContainerClass}
              style={{ 
                width: getImageWidth(),
                borderColor: innerBorderColor,
                ...(isExpanded ? {} : { aspectRatio: '16 / 9' })
              }}
            >
              {hasImage ? (
                <img
                  src={src}
                  alt=""
                  className={isExpanded ? "w-full block" : "h-full w-full object-cover"}
                  draggable={false}
                  onError={() => setImgOk(false)}
                />
              ) : (
                <ImagePlaceholder />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-5">
          <div className="min-w-0 flex-1">
            {label && (
              <div className="text-[20px] tracking-[0.18em] uppercase font-medium" style={{ fontFamily: fontPreset?.subtitle, color: pc ? pc.el.widgetLabel : 'rgba(255,255,255,0.60)' }}>{label}</div>
            )}
            {caption && (
              <div className="mt-3 text-[26px] leading-relaxed whitespace-pre-line" style={{ fontFamily: fontPreset?.body, color: pc ? pc.el.widgetCaption : 'rgba(255,255,255,0.80)' }}>{renderFormattedText(caption)}</div>
            )}
          </div>
          <div 
            className={`shrink-0 ${imageContainerClass}`}
            style={{ 
              width: getImageWidth(),
              borderColor: innerBorderColor,
              aspectRatio: aspectRatio === '4:5' ? '4 / 5' : '1 / 1'
            }}
          >
            {hasImage ? (
              <img
                src={src}
                alt=""
                className="h-full w-full object-cover"
                draggable={false}
                onError={() => setImgOk(false)}
              />
            ) : (
              <ImagePlaceholder />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Video placeholder when no video is uploaded
 */
function VideoPlaceholder() {
  return (
    <div className="h-full w-full flex items-center justify-center bg-black/30 text-white/30">
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    </div>
  );
}

/**
 * Video/media widget with label, caption, and configurable aspect ratio & size
 */
export function VideoWidget({ label, caption, src, aspectRatio = '16:9', size = 'large', blur, opacity, showBorder = true, fontPreset, expandVertical = false, pc }) {
  const [videoOk, setVideoOk] = React.useState(true);
  const hasVideo = !!src && videoOk;
  
  React.useEffect(() => {
    setVideoOk(true);
  }, [src]);

  const isLandscape = aspectRatio === '16:9';
  const isExpanded = isLandscape && expandVertical;

  const getVideoWidth = () => {
    if (isLandscape) {
      return size === 'large' ? '100%' : '75%';
    } else {
      return size === 'large' ? '50%' : '30%';
    }
  };

  const innerBorderColor = pc ? pc.tint(0.10) : 'rgba(255,255,255,0.10)';
  const videoContainerClass = "overflow-hidden rounded-2xl border";

  return (
    <div 
      key={`vid-${aspectRatio}-${size}-${expandVertical}`}
      className={`mt-6 rounded-2xl p-6${widgetBorderStyle(showBorder, pc)}`}
      style={{ ...getWidgetStyle(blur, opacity, pc), borderColor: showBorder ? widgetBorderColor(pc) : undefined }}
    >
      {isLandscape ? (
        <div className="flex flex-col gap-4">
          <div className="min-w-0">
            {label && (
              <div className="text-[20px] tracking-[0.18em] uppercase font-medium" style={{ fontFamily: fontPreset?.subtitle, color: pc ? pc.el.widgetLabel : 'rgba(255,255,255,0.60)' }}>{label}</div>
            )}
            {caption && (
              <div className="mt-2 text-[26px] leading-relaxed whitespace-pre-line" style={{ fontFamily: fontPreset?.body, color: pc ? pc.el.widgetCaption : 'rgba(255,255,255,0.80)' }}>{renderFormattedText(caption)}</div>
            )}
          </div>
          <div className={`${size === 'small' ? 'flex justify-center' : ''}`}>
            <div 
              className={videoContainerClass}
              style={{ 
                width: getVideoWidth(),
                borderColor: innerBorderColor,
                ...(isExpanded ? {} : { aspectRatio: '16 / 9' })
              }}
            >
              {hasVideo ? (
                <video
                  src={src}
                  className={isExpanded ? "w-full block" : "h-full w-full object-cover"}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onError={() => setVideoOk(false)}
                />
              ) : (
                <VideoPlaceholder />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-5">
          <div className="min-w-0 flex-1">
            {label && (
              <div className="text-[20px] tracking-[0.18em] uppercase font-medium" style={{ fontFamily: fontPreset?.subtitle, color: pc ? pc.el.widgetLabel : 'rgba(255,255,255,0.60)' }}>{label}</div>
            )}
            {caption && (
              <div className="mt-3 text-[26px] leading-relaxed whitespace-pre-line" style={{ fontFamily: fontPreset?.body, color: pc ? pc.el.widgetCaption : 'rgba(255,255,255,0.80)' }}>{renderFormattedText(caption)}</div>
            )}
          </div>
          <div 
            className={`shrink-0 ${videoContainerClass}`}
            style={{ 
              width: getVideoWidth(),
              borderColor: innerBorderColor,
              aspectRatio: aspectRatio === '4:5' ? '4 / 5' : '1 / 1'
            }}
          >
            {hasVideo ? (
              <video
                src={src}
                className="h-full w-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                onError={() => setVideoOk(false)}
              />
            ) : (
              <VideoPlaceholder />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
